/* eslint-disable max-lines-per-function */
/* eslint-disable max-len */
import React from "react";
import {
  ArrowDropDownCircle,
  AttachMoney,
  CloudUpload,
  DateRange,
  Description,
  Email,
  Event,
  FormatListBulleted,
  Group,
  HelpOutline,
  Home,
  Lock,
  Phone,
  Print,
  Public,
  Reorder,
  TextFields,
  ToggleOn,
} from "@mui/icons-material";
import { BuilderQuestion, BuilderSection, identityToKey } from "./form-builder-utils";

export type FormQuestionVariant = {
  id: string;
  questionName: string;
  valueType: string;
  version: number;
  title: string;
  usedBy: number;
  rawData: string;
};

export type FormQuestionVariantsResponse = {
  formQuestionVariants: FormQuestionVariant[];
};

export type QuestionGroupsBySection = {
  sectionGroups: { section: BuilderSection; questions: BuilderQuestion[] }[];
  childLinkedQuestions: BuilderQuestion[];
  unassignedQuestions: BuilderQuestion[];
  sectionAssignableIdentityOptions: string[];
};

export const UNASSIGNED_QUESTIONS_TAB = "__unassigned_questions__";
export const CHILD_LINKED_QUESTIONS_TAB = "__child_linked_questions__";
export const QUESTION_TYPES_BUILDER_TAB = "__question_types_builder_tab__";
export const QUESTIONS_BUILDER_TAB = "__questions_builder_tab__";
export const SECTIONS_BUILDER_TAB = "__sections_builder_tab__";
export const TREE_BUILDER_TAB = "__tree_builder_tab__";

export const getQuestionIdentityKey = (question: BuilderQuestion): string =>
  identityToKey(
    [
      question.name.trim(),
      ...question.contextText
        .split(",")
        .map((value) => value.trim())
        .filter(Boolean),
    ].filter(Boolean),
  );

export const removeArrayChildren = (data: Record<string, unknown>): Record<string, unknown> =>
  Object.keys(data).reduce<Record<string, unknown>>((acc, key) => {
    if (key !== "children") acc[key] = data[key];
    return acc;
  }, {});

export const buildQuestionGroupsBySection = (
  questions: BuilderQuestion[],
  sections: BuilderSection[],
): QuestionGroupsBySection => {
  const questionsByIdentity = questions.reduce<Record<string, BuilderQuestion[]>>((acc, question) => {
    const identityKey = getQuestionIdentityKey(question);
    if (!acc[identityKey]) acc[identityKey] = [];
    acc[identityKey].push(question);
    return acc;
  }, {});

  const sectionAssignedQuestionIds = new Set<string>();

  const sectionGroups = sections.map((section) => {
    const seenQuestionIds = new Set<string>();
    const sectionQuestions = section.questionKeys
      .flatMap((identityKey) => questionsByIdentity[identityKey] || [])
      .filter((question) => {
        if (seenQuestionIds.has(question.draftId)) return false;
        seenQuestionIds.add(question.draftId);
        sectionAssignedQuestionIds.add(question.draftId);
        return true;
      });

    return { section, questions: sectionQuestions };
  });

  const childLinkedQuestionIds = questions.reduce((acc, question) => {
    question.childrenKeys.forEach((childKey) => {
      (questionsByIdentity[childKey] || []).forEach((childQuestion) => acc.add(childQuestion.draftId));
    });
    return acc;
  }, new Set<string>());

  const questionsOutsideSections = questions.filter(
    (question) => !sectionAssignedQuestionIds.has(question.draftId),
  );

  const childLinkedQuestions = questionsOutsideSections.filter(
    (question) => childLinkedQuestionIds.has(question.draftId),
  );
  const unassignedQuestions = questionsOutsideSections.filter(
    (question) => !childLinkedQuestionIds.has(question.draftId),
  );
  const sectionAssignableIdentityOptions = Array.from(
    new Set(unassignedQuestions.map(getQuestionIdentityKey).filter(Boolean)),
  );

  return {
    sectionGroups,
    childLinkedQuestions,
    unassignedQuestions,
    sectionAssignableIdentityOptions,
  };
};

export const getQuestionsInTab = (
  questionGroupsBySection: QuestionGroupsBySection,
  selectedQuestionTab: string,
): BuilderQuestion[] => {
  if (selectedQuestionTab === UNASSIGNED_QUESTIONS_TAB) {
    return questionGroupsBySection.unassignedQuestions;
  }

  if (selectedQuestionTab === CHILD_LINKED_QUESTIONS_TAB) {
    return questionGroupsBySection.childLinkedQuestions;
  }

  return questionGroupsBySection.sectionGroups.find(
    ({ section }) => section.draftId === selectedQuestionTab,
  )?.questions || [];
};

export const getSectionQuestionOptions = (
  sectionDraftId: string,
  sectionQuestionKeys: string[],
  sections: BuilderSection[],
  questionIdentityOptions: string[],
): string[] => {
  const sectionSelectedKeys = new Set(sectionQuestionKeys);

  return questionIdentityOptions.filter(
    (identityKey) =>
      sectionSelectedKeys.has(identityKey) ||
      !sections.some(
        (section) =>
          section.draftId !== sectionDraftId &&
          section.questionKeys.includes(identityKey),
      ),
  );
};

export const getArrayChildQuestionOptions = (
  parentQuestionKey: string,
  selectedChildKeys: string[],
  sectionAssignableIdentityOptions: string[],
): string[] =>
  Array.from(new Set([...sectionAssignableIdentityOptions, ...selectedChildKeys]))
    .filter((identityKey) => identityKey !== parentQuestionKey);

export const getQuestionTypeIcon = (questionType: string): React.ReactElement => {
  switch (questionType) {
    case "address":
      return <Home fontSize="small" />;
    case "array":
      return <Reorder fontSize="small" />;
    case "boolean":
      return <ToggleOn fontSize="small" />;
    case "date":
      return <Event fontSize="small" />;
    case "date_range":
      return <DateRange fontSize="small" />;
    case "email":
      return <Email fontSize="small" />;
    case "encrypted_text":
      return <Lock fontSize="small" />;
    case "fax_number":
      return <Print fontSize="small" />;
    case "formatted":
      return <Description fontSize="small" />;
    case "household_member":
      return <Group fontSize="small" />;
    case "money":
      return <AttachMoney fontSize="small" />;
    case "multiple":
      return <FormatListBulleted fontSize="small" />;
    case "phone_number":
      return <Phone fontSize="small" />;
    case "select":
      return <ArrowDropDownCircle fontSize="small" />;
    case "text":
      return <TextFields fontSize="small" />;
    case "upload":
      return <CloudUpload fontSize="small" />;
    case "us_state":
      return <Public fontSize="small" />;
    default:
      return <HelpOutline fontSize="small" />;
  }
};
